// pending-0.1.19 bump for Cart Bootstrap port
/**
 * CartContext — v3 port of the SPFx `CartProvider` + `useCart` hook.
 *
 * Source of truth (read-only):
 *   C:/Projects/retail-account-opening/spfx-account-opening-v2/.../context/CartContext.tsx
 *
 * Port differences (intentional):
 *   - The SPFx version holds the entire `Cart` object in a single React
 *     reducer (people / products / ownership / beneficiaries / services /
 *     notes). In v3 the canonical cart is `CartDto`, which is SQL-backed
 *     and where every "domain" (people / products / …) is a stringified
 *     JSON envelope that the step components own. So CartContext here
 *     holds:
 *       1. the active CartDto (set via `setCart` / `hydrate`)
 *       2. a small in-memory patch buffer that the debounced saver drains
 *          every 5 s (same cadence as SPFx's SessionService debounce)
 *       3. the pipeline snapshot + the PipelineService instance
 *   - Consumers that used to dispatch `ADD_PERSON` etc now call `patch({
 *     peopleJson: JSON.stringify(newPeople) })` — identical to what
 *     PeopleStep already does via `client.retail.carts.update`. Both paths
 *     coexist (PeopleStep still supports its direct PATCH flow).
 *   - 409 (cart-version-conflict) triggers `hydrate()` — the server's
 *     authoritative cart is fetched, local state rebases, the patch
 *     buffer is kept so the next debounce can retry.
 *
 * Downstream modules (RCM, RAM, RIM, RCD, RSM) can mount `<CartProvider/>`
 * around their routes to share the same active cart session.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  CartDto,
  CreateCartPayload,
  UpdateCartPayload,
  PipelineSnapshot,
} from '@retail-ops/shared-types';
import { useApiClient } from './ApiClientContext';
import { CartStateService } from '@/services/CartStateService';
import { PipelineService } from '@/services/PipelineService';

// ─── Cart state machine ─────────────────────────────────────────────────────
//
// Mirrors the SPFx "phase" union. Maps 1:1 to `CartStatus` enum values (the
// wire shape) with a couple of UI-only extras:
//   - 'idle'     — provider mounted, no cart yet
//   - 'loading'  — fetching/creating
//   - 'ready'    — `cart !== null`, patch buffer flushed
//   - 'saving'   — debounce in-flight
//   - 'conflict' — 409 received, UI shows rebase banner
//   - 'error'    — fatal (non-conflict) — UI shows retry
//
// These drive `rao-record__footer` spinners and the "auto-saved at" text
// in CartSidebar.

export type CartProviderState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'saving'
  | 'conflict'
  | 'error';

export interface CartContextValue {
  // ─── State ────────────────────────────────────────────────────────────────
  cart: CartDto | null;
  snapshot: PipelineSnapshot | null;
  state: CartProviderState;
  error: string | null;
  dirty: boolean;
  lastSavedAt: Date | null;

  // ─── Lifecycle ────────────────────────────────────────────────────────────
  /** Create a brand-new cart and adopt it as the active one. */
  createCart: (payload: CreateCartPayload) => Promise<CartDto | null>;
  /** Load an existing cart by id and adopt it. */
  hydrate: (cartId: string) => Promise<CartDto | null>;
  /**
   * Adopt a server-fresh CartDto another fetch path already owns (e.g.
   * CartDetail's `useCart` record fetch) WITHOUT re-fetching and WITHOUT
   * touching the dirty patch buffer. Stale-guarded: a same-cart adopt whose
   * `version` is not newer than the context's copy is ignored, so an
   * optimistic local patch or a fresher flush response is never rolled back
   * by a slower GET. This is the sync channel that keeps CartSidebar (the
   * cart-inventory rail) live as the RAO steps PATCH the cart directly —
   * pre-fix the rail froze at the mount-time snapshot (live 2026-06-04
   * pilot blocker: counts stuck at 0, Review CTA never enabled).
   */
  adopt: (cart: CartDto) => void;
  /** Drop the active cart without saving pending mutations. */
  clear: () => void;

  // ─── Mutations (optimistic; debounced save at 5 s) ────────────────────────
  /**
   * Merge the patch into the local cart + buffer for the next debounced save.
   * Any caller can short-circuit the debounce via `flush()`.
   */
  patch: (next: UpdateCartPayload) => void;
  /** Force an immediate save, returning the server's cart on success. */
  flush: () => Promise<CartDto | null>;

  // ─── Pipeline snapshot (mirrors `Carts.pipelineSnapshotJson`) ─────────────
  setSnapshot: (snapshot: PipelineSnapshot | null) => void;

  // ─── Helpers ──────────────────────────────────────────────────────────────
  service: CartStateService;
  pipeline: PipelineService;
}

const CartContext = createContext<CartContextValue | null>(null);

/** Save cadence when the cart is dirty. Matches SPFx SessionService. */
const DEBOUNCE_MS = 5_000;

export function CartProvider({ children }: { children: ReactNode }): JSX.Element {
  const api = useApiClient();
  const service = useMemo(() => new CartStateService(api), [api]);
  const pipeline = useMemo(() => new PipelineService(), []);

  // ─── Core state ─────────────────────────────────────────────────────────
  const [cart, setCart] = useState<CartDto | null>(null);
  const [snapshot, setSnapshotState] = useState<PipelineSnapshot | null>(null);
  const [state, setState] = useState<CartProviderState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Patch buffer — merged onto `cart` locally, flushed on debounce.
  const pendingRef = useRef<UpdateCartPayload>({});
  const dirtyRef = useRef<boolean>(false);
  const [dirtyTick, setDirtyTick] = useState<number>(0); // re-render trigger
  const dirty = dirtyRef.current || dirtyTick > 0;

  // Debounce timer
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AbortController shared for the current in-flight save so React-strict-mode
  // double-effects don't race.
  const inFlightRef = useRef<AbortController | null>(null);

  const clearTimer = (): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // ─── Save path ──────────────────────────────────────────────────────────

  const flush = useCallback(async (): Promise<CartDto | null> => {
    if (!cart) return null;
    if (!dirtyRef.current) return cart;

    clearTimer();
    inFlightRef.current?.abort();
    const controller = new AbortController();
    inFlightRef.current = controller;

    const buffer: UpdateCartPayload = {
      ...pendingRef.current,
      expectedVersion: cart.version,
    };
    pendingRef.current = {};
    dirtyRef.current = false;
    setDirtyTick((t) => t + 1); // flip to re-render "saved" state

    setState('saving');

    const res = await service.updateCart(cart.cartId, buffer, { signal: controller.signal });

    if (res.success && res.data) {
      setCart(res.data);
      setState('ready');
      setError(null);
      setLastSavedAt(new Date());
      return res.data;
    }

    // Conflict → re-hydrate, keep buffer for retry.
    if (res.errorCode === 'cart-version-conflict' || res.error?.includes('409')) {
      pendingRef.current = { ...buffer, ...pendingRef.current };
      dirtyRef.current = true;
      setState('conflict');
      setError(res.error ?? 'Version conflict');
      // Re-hydrate from server — caller's next patch() restarts the save loop.
      const fresh = await service.getCart(cart.cartId);
      if (fresh.success && fresh.data) {
        setCart(fresh.data);
      }
      return null;
    }

    // Generic error — keep dirty, surface for UI retry.
    pendingRef.current = { ...buffer, ...pendingRef.current };
    dirtyRef.current = true;
    setState('error');
    setError(res.error ?? 'Save failed');
    return null;
  }, [cart, service]);

  // Schedule the debounced save whenever the buffer turns dirty.
  useEffect(() => {
    if (!dirty || !cart) return;
    clearTimer();
    timerRef.current = setTimeout(() => {
      void flush();
    }, DEBOUNCE_MS);
    return (): void => {
      clearTimer();
    };
    // dirty is read via ref-tick; intentionally exhaustive.
  }, [dirtyTick, cart, flush, dirty]);

  // Abort any pending save on unmount.
  useEffect(() => {
    return (): void => {
      clearTimer();
      inFlightRef.current?.abort();
    };
  }, []);

  // ─── Public API ─────────────────────────────────────────────────────────

  const patch = useCallback((next: UpdateCartPayload): void => {
    pendingRef.current = { ...pendingRef.current, ...next };
    dirtyRef.current = true;

    // Apply optimistically to the local cart.
    setCart((prev) => (prev ? { ...prev, ...next } : prev));
    setDirtyTick((t) => t + 1);
  }, []);

  const createCart = useCallback(
    async (payload: CreateCartPayload): Promise<CartDto | null> => {
      setState('loading');
      setError(null);
      const res = await service.createCart(payload);
      if (res.success && res.data) {
        setCart(res.data);
        setSnapshotState(hydrateSnapshot(res.data));
        setState('ready');
        setLastSavedAt(new Date());
        return res.data;
      }
      setState('error');
      setError(res.error ?? 'Unable to create cart');
      return null;
    },
    [service],
  );

  const hydrate = useCallback(
    async (cartId: string): Promise<CartDto | null> => {
      setState('loading');
      setError(null);
      const res = await service.getCart(cartId);
      if (res.success && res.data) {
        setCart(res.data);
        setSnapshotState(hydrateSnapshot(res.data));
        setState('ready');
        setError(null);
        return res.data;
      }
      setState('error');
      setError(res.error ?? 'Unable to load cart');
      return null;
    },
    [service],
  );

  const adopt = useCallback(
    (next: CartDto): void => {
      // Stale-guard — see CartContextValue.adopt docs. `>=` (not `>`)
      // keeps equal-version copies out so optimistic local patches applied
      // via patch() (which mutate the cart without bumping version) survive.
      if (cart && cart.cartId === next.cartId && cart.version >= next.version) return;
      setCart(next);
      setSnapshotState(hydrateSnapshot(next));
      // Settle into 'ready' unless a flush is mid-flight ('saving') — the
      // saving indicator must reflect the in-flight PATCH, not this adopt.
      setState((s) => (s === 'saving' ? s : 'ready'));
      setError(null);
    },
    [cart],
  );

  const clear = useCallback((): void => {
    clearTimer();
    inFlightRef.current?.abort();
    pendingRef.current = {};
    dirtyRef.current = false;
    setCart(null);
    setSnapshotState(null);
    setState('idle');
    setError(null);
    setLastSavedAt(null);
  }, []);

  const setSnapshot = useCallback((snap: PipelineSnapshot | null): void => {
    setSnapshotState(snap);
    if (snap) {
      patch({ pipelineSnapshotJson: JSON.stringify(snap) });
    }
  }, [patch]);

  const value = useMemo<CartContextValue>(
    () => ({
      cart,
      snapshot,
      state,
      error,
      dirty,
      lastSavedAt,
      createCart,
      hydrate,
      adopt,
      clear,
      patch,
      flush,
      setSnapshot,
      service,
      pipeline,
    }),
    [cart, snapshot, state, error, dirty, lastSavedAt, createCart, hydrate, adopt, clear, patch, flush, setSnapshot, service, pipeline],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/**
 * Primary consumer hook. Throws if used outside a CartProvider so misuse
 * surfaces loudly at mount time.
 */
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

/**
 * Softer variant for widgets that live outside a cart (e.g. CommandPalette)
 * and just want to know whether a cart is currently active without
 * requiring the provider.
 */
export function useOptionalCart(): CartContextValue | null {
  return useContext(CartContext);
}

// ─── internals ──────────────────────────────────────────────────────────────

function hydrateSnapshot(cart: CartDto): PipelineSnapshot | null {
  if (!cart.pipelineSnapshotJson) return null;
  try {
    const raw = JSON.parse(cart.pipelineSnapshotJson) as unknown;
    if (raw && typeof raw === 'object' && Array.isArray((raw as PipelineSnapshot).steps)) {
      return raw as PipelineSnapshot;
    }
  } catch {
    // fall through
  }
  return null;
}
